"use client";

import { Menu as MenuPrimitive } from "@base-ui/react/menu";
import { Check, ChevronDown } from "lucide-react";
import Link from "next/link";
import { useCallback, useState } from "react";

import { HEADER_COPY } from "../_lib/copy-shared";
import { pick, type SupplyLang } from "../_lib/lang";
import { withOutlet } from "../_lib/with-outlet";

/**
 * The header's outlet switcher — the pill, promoted from a link-to-the-picker
 * to a dropdown (2026-08-19 operator flow rework: "ill be on one page and it
 * says outlet then another page and the picker is gone"). One consistent
 * control on every outlet-scoped page; picking an outlet lands on the SAME
 * page with `?outlet=`, and the tab bar's cookie sync makes every later page
 * follow. The full-page picker survives only for first-landing (no resolved
 * outlet) — it is no longer the switch mechanism, so the pill no longer
 * bounces through `?pick=1`.
 *
 * Built on the same @base-ui/react Menu primitives as SupplyUserMenu, for the
 * same reasons (see that file's docblock: scoped tokens, portal container,
 * hover-open that cannot break touch). Rows are next/link items — a menu of
 * NAVIGATIONS, not state toggles, so they are plain menuitems with an
 * aria-current mark rather than menuitemradio: activating one leaves the
 * page, which is not the "select within an open menu" contract RadioItem
 * models.
 *
 * `groupHref` (food-cost's "HQ · all outlets") renders as the first row when
 * given — only pages with a REAL group view pass it; pages that require one
 * outlet (order, count, receive…) never do.
 */
export type SupplyOutletMenuConfig = {
  /** The page's own path — each row navigates back to it with `?outlet=`. */
  basePath: string;
  /** Outlet codes this page can serve, in the page's own door order. */
  codes: string[];
  /** Explicit group-view URL, for pages that support one. */
  groupHref?: string;
  /** True while the page is showing its group view — moves the check mark. */
  groupActive?: boolean;
};

export function SupplyOutletMenu({
  menu,
  currentCode,
  phoneLabel,
  fullLabel,
  pillClass,
  lang,
}: {
  menu: SupplyOutletMenuConfig;
  /** The REAL `outlets.code` of the outlet on screen (check-mark anchor). */
  currentCode?: string;
  phoneLabel?: string;
  fullLabel?: string;
  pillClass: string;
  lang: SupplyLang;
}) {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const anchorRef = useCallback((node: HTMLSpanElement | null) => {
    setContainer(node?.closest<HTMLElement>("[data-supply]") ?? null);
  }, []);
  // min-h-[44px] is written LITERALLY inside each Item's own className below,
  // not through this constant — touch-target-floor.test.ts reads literal
  // Tailwind tokens out of each tag's OWN source text, and a size class one
  // hop away in `${itemClass}` is invisible to it either way (SupplyUserMenu
  // learned this live; see its docblock).
  const itemClass =
    "flex items-center gap-2 rounded-[9px] px-2.5 text-[13px] font-bold text-[var(--body)] outline-none data-highlighted:bg-[var(--raised)] data-highlighted:text-[var(--text)]";
  const markClass = "size-[15px] shrink-0 text-[var(--success)]";
  const markSpacer = <span className="size-[15px] shrink-0" aria-hidden />;
  return (
    <span ref={anchorRef} className="contents">
      <MenuPrimitive.Root modal={false}>
        <MenuPrimitive.Trigger
          aria-label={`${fullLabel ?? ""} — ${pick(lang, HEADER_COPY.outletMenu)}`.trim()}
          title={pick(lang, HEADER_COPY.outletMenu)}
          openOnHover
          delay={120}
          className={`${pillClass} relative inline-flex items-center justify-center gap-1 transition active:scale-[0.97] data-popup-open:ring-2 data-popup-open:ring-[var(--accent)]/40 before:absolute before:inset-x-0 before:top-1/2 before:h-11 before:-translate-y-1/2 before:content-['']`}
        >
          {/* Same phone/desk label split as the static chip — the menu only
              adds the chevron, the identity reads identically. */}
          <span className="min-w-0 truncate lg:hidden">{phoneLabel}</span>
          <span className="hidden min-w-0 truncate lg:inline">{fullLabel}</span>
          <ChevronDown className="size-[11px] shrink-0" strokeWidth={3} aria-hidden />
        </MenuPrimitive.Trigger>
        <MenuPrimitive.Portal container={container ?? undefined}>
          <MenuPrimitive.Positioner className="isolate z-50 outline-none" side="bottom" align="end" sideOffset={8}>
            <MenuPrimitive.Popup className="min-w-[190px] rounded-[16px] border border-[var(--border)] bg-[var(--surface)] p-1.5 text-[var(--text)] shadow-[var(--shadow-card)] outline-none">
              <p className="px-2.5 pb-1 pt-1.5 text-[10px] font-extrabold uppercase tracking-[0.08em] text-[var(--faint)]">
                {pick(lang, HEADER_COPY.outletMenu)}
              </p>
              {menu.groupHref ? (
                <MenuPrimitive.Item
                  render={<Link href={menu.groupHref} prefetch={false} />}
                  aria-current={menu.groupActive ? "true" : undefined}
                  className={`min-h-[44px] ${itemClass}`}
                >
                  {menu.groupActive ? <Check className={markClass} strokeWidth={3} aria-hidden /> : markSpacer}
                  {pick(lang, HEADER_COPY.outletGroupAll)}
                </MenuPrimitive.Item>
              ) : null}
              {menu.codes.map((code) => {
                const active = !menu.groupActive && code === currentCode;
                return (
                  <MenuPrimitive.Item
                    key={code}
                    render={<Link href={withOutlet(menu.basePath, code)} prefetch={false} />}
                    aria-current={active ? "true" : undefined}
                    className={`min-h-[44px] ${itemClass}`}
                  >
                    {active ? <Check className={markClass} strokeWidth={3} aria-hidden /> : markSpacer}
                    {code}
                  </MenuPrimitive.Item>
                );
              })}
            </MenuPrimitive.Popup>
          </MenuPrimitive.Positioner>
        </MenuPrimitive.Portal>
      </MenuPrimitive.Root>
    </span>
  );
}
